import { addDays, addMonths, format } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency, EProductsInsurance } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { getMinMaxPaymentDates } from '@src/services/paymentDate-utils'
import { min } from 'lodash-es'
import { baseWorksheetSchema } from './BaseWorksheetSchema'
import { FinancingConfigDto, WarrantyPriceTierDto } from './FinancingConfigDto'
import {
  IFinanceBaseWorksheet,
  IFinanceBaseWorksheetDecisionDTOSchema,
  IFinanceBaseWorksheetRevision,
} from './IFinanceBaseWorksheetSchema'
import { MerchantPayment } from './MerchantPayment'

const MIN_TERM = 12

// A product block is all-or-nothing: as soon as one field is filled, the others become required.
// The provider is excluded: it can be forced by the system (SymTech prime) and is validated in the component.
interface InsuranceBlockValues {
  amount?: number | null
  term?: number | null
  police?: string | null
  endDate?: string | null
  provider?: string | null
}

const requiredWhenBlockStarted = (ctx: yup.TestContext<yup.AnyObject>): boolean | yup.ValidationError => {
  const values = ctx.parent as InsuranceBlockValues
  const anyFieldProvided = !!values?.amount || !!values?.term || !!values?.police || !!values?.endDate
  const field = ctx.path.split('.')[1] as keyof InsuranceBlockValues
  if (anyFieldProvided && !values[field])
    return ctx.createError({ path: ctx.path, message: t('mixed.required', { ns: 'validation' }) })
  return true
}

export const baseInsuranceSchema = yup.object({
  provider: yup.string().nullable().default(null),
  police: yup
    .string()
    .nullable()
    .default(null)
    .test(function () {
      return requiredWhenBlockStarted(this)
    }),
  term: yup
    .integer()
    .min(MIN_TERM)
    .test(function () {
      return requiredWhenBlockStarted(this)
    }),
  amount: yup.double().test(function () {
    return requiredWhenBlockStarted(this)
  }),
  endDate: yup.string().nullable().default(null).dateOnly(),
})

const withConditionalEndDate = (schema: typeof baseInsuranceSchema, isRequired: boolean) => {
  if (isRequired) {
    return schema.shape({
      endDate: yup
        .string()
        .dateOnly()
        .required()
        .when('term', ([term], schema) => {
          if (term && !isNaN(Number(term))) {
            const date = new Date()
            const minDate = format(addMonths(date, Number(term)), 'yyyy-MM-dd')
            const maxDate = format(addDays(addMonths(date, Number(term)), 21), 'yyyy-MM-dd')

            return schema.default(minDate).test('min-date', (val, ctx) => {
              if (!val) return true
              if (val < minDate) {
                return ctx.createError({
                  message: t('common.errors.invalidMinDate', { minValue: minDate }),
                })
              }
              if (val > maxDate) {
                return ctx.createError({
                  message: t('common.errors.invalidMaxDate', { maxValue: maxDate }),
                })
              }
              return true
            })
          }
          return schema.default(format(new Date(), 'yyyy-MM-dd'))
        }),
    })
  }
  return schema
}

export type ProductsInsurance = yup.InferSubmit<typeof baseInsuranceSchema>

export type ProductsInsurancePlan = ProductsInsurance & {
  providerName: string
  type: EProductsInsurance
}

const resolveInsuranceMaxTerm = (schema: yup.ObjectSchema<ProductsInsurance>, vehicleFinancingTerm: number) => {
  if (vehicleFinancingTerm)
    (schema.fields.term as yup.NumberSchema<number | null | undefined, yup.AnyObject, null, 'd'>).max(
      vehicleFinancingTerm,
    )

  return schema
}

const resolveMaxTerm = (schema: yup.NumberSchema<number | undefined>, values: ProductsInsurance[]) => {
  const terms: number[] = []

  values.forEach((e) => {
    if (e) if (e.term) terms.push(e.term)
  })

  const max = min(terms)

  if (max) {
    return schema.max(max)
  }

  return schema
}
/**
 * Resolves the maximum allowed extended-warranty amount for a given vehicle selling price.
 * Mirrors the backend (ProductsWorksheetValidationService.GetMaxWarrantyAmountForVehiclePrice)
 */
export const getWarrantyMaxForVehiclePrice = (
  tiers: WarrantyPriceTierDto[] | undefined,
  vehicleSellingAmount: number | null | undefined,
): number | null => {
  if (!tiers?.length || vehicleSellingAmount == null) return null
  const coveringTier = tiers
    .filter((t) => t.maxVehiclePrice != null && vehicleSellingAmount <= t.maxVehiclePrice)
    .sort((a, b) => a.maxVehiclePrice! - b.maxVehiclePrice!)[0]
  if (coveringTier) return coveringTier.maxWarrantyAmount
  const catchAllTier = tiers.find((t) => t.maxVehiclePrice == null)
  return catchAllTier ? catchAllTier.maxWarrantyAmount : null
}

/**
 * Role-aware product-cap validation options. When the feature is on, the worksheet must respect the
 * warranty tier cap (Rule A) and the products total cap (Rule B). A SupervisorCreditAgent gets the
 * configured allowance added on top of each cap; the backend remains the final gate.
 */
export interface ProductsCapValidationOptions {
  config?: FinancingConfigDto
  featureEnabled?: boolean
  isSupervisor?: boolean
}

export const buildProductsWorksheetDtoSchema = (
  replacementOrGapInsuranceTerm: boolean,
  creditInsuranceTerm: boolean,
  capOptions: ProductsCapValidationOptions = {},
) => {
  const productsWorksheetSchema = yup
    .object({
      creditApplicationId: yup.string(),
      amountRequested: yup.double().required().positive(),
      includeInsurance: yup.boolean().default(false),
      merchantPayments: yup.mixed<MerchantPayment[]>().default([]),
      paymentPlanId: yup.string().required(),
      deliveryOn: yup.string().validDate().default(new Date().toDateString()).nullable().required(),
      firstPaymentOn: yup
        .string()
        .dateOnly()
        .default(() => {
          const date = new Date()
          date.setDate(date.getDate() + 2)
          return format(date, 'yyyy-MM-dd')
        })
        .required()
        .test('deliveryOn-range', (val, ctx) => {
          const deliveryOn = (ctx.parent as ProductsWorksheet).deliveryOn
          const paymentFrequency = (ctx.parent as { paymentFrequency: EPaymentFrequency | null }).paymentFrequency
          if (!val || !deliveryOn || !paymentFrequency) return true
          const [minDate, maxDate] = getMinMaxPaymentDates(deliveryOn, paymentFrequency)
          if (val < formatDate(minDate))
            return ctx.createError({ message: t('common.errors.invalidMinDate', { minValue: formatDate(minDate) }) })
          if (val > formatDate(maxDate))
            return ctx.createError({ message: t('common.errors.invalidMaxDate', { maxValue: formatDate(maxDate) }) })
          return true
        }),
      term: (baseWorksheetSchema.fields.term as yup.NumberSchema)
        .required()
        .when(
          ['extendedWarranty', 'replacementOrGapInsurance', 'creditInsurance'],
          (values: ProductsInsurance[], schema: yup.NumberSchema<number | undefined>) => {
            return resolveMaxTerm(schema, values)
          },
        ),
      vehicleFinancingTerm: yup.integer().nullable().required().min(MIN_TERM).max(240),

      vehicleSellingAmount: capOptions.featureEnabled
        ? yup.double().nullable().required().positive()
        : yup.double().nullable().default(null),

      // Extended warranty has no end-date
      extendedWarranty: baseInsuranceSchema
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),

      creditInsurance: withConditionalEndDate(baseInsuranceSchema, creditInsuranceTerm)
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),

      replacementOrGapInsurance: withConditionalEndDate(baseInsuranceSchema, replacementOrGapInsuranceTerm)
        .when('vehicleFinancingTerm', ([vehicleFinancingTerm], schema) => {
          return resolveInsuranceMaxTerm(schema, vehicleFinancingTerm as number)
        })
        .nullable(),
    })
    .concat(baseWorksheetSchema.omit(['firstPaymentOn', 'lenderFee']))
    // Rule A — extended warranty capped by the vehicle-price tier. A supervisor gets the configured allowance on top.
    .test('warranty-tier-cap', (value, ctx) => {
      const { featureEnabled, isSupervisor, config } = capOptions
      if (!featureEnabled) return true
      const worksheet = value as ProductsWorksheet
      const warrantyAmount = worksheet.extendedWarranty?.amount
      const tierCap = getWarrantyMaxForVehiclePrice(
        config?.warrantyMaxByVehiclePriceTier,
        worksheet.vehicleSellingAmount,
      )
      if (warrantyAmount == null || tierCap == null) return true
      const maxWarranty = tierCap + (isSupervisor ? (config?.supervisorWarrantyAmountAllowance ?? 0) : 0)
      if (warrantyAmount > maxWarranty) {
        return ctx.createError({
          path: 'extendedWarranty.amount',
          message: t('worksheet.errors.warrantyExceedsTierCap', { max: maxWarranty }),
        })
      }
      return true
    })
    // Rule B — sum of the three financed products capped by maxProductsTotalAmount. A supervisor gets the allowance on top.
    .test('products-total-cap', (value, ctx) => {
      const { featureEnabled, isSupervisor, config } = capOptions
      if (!featureEnabled) return true
      const baseMax = config?.maxProductsTotalAmount
      if (!baseMax) return true
      const maxTotal = baseMax + (isSupervisor ? (config?.supervisorProductsTotalAllowance ?? 0) : 0)
      const worksheet = value as ProductsWorksheet
      const total =
        (worksheet.creditInsurance?.amount ?? 0) +
        (worksheet.replacementOrGapInsurance?.amount ?? 0) +
        (worksheet.extendedWarranty?.amount ?? 0)
      if (total > maxTotal) {
        return ctx.createError({
          path: 'amountRequested',
          message: t('worksheet.errors.productsTotalExceedsCap', { max: maxTotal }),
        })
      }
      return true
    })
  return productsWorksheetSchema
}

export type ProductsWorksheet = IFinanceBaseWorksheet & {
  creditApplicationId: string
  creditInsurance: ProductsInsurance
  replacementOrGapInsurance: ProductsInsurance
  extendedWarranty: ProductsInsurance
  vehicleFinancingTerm: number
  vehicleSellingAmount: number | null
}

export type ProductsWorksheetDto = yup.InferSubmit<ReturnType<typeof buildProductsWorksheetDtoSchema>>

export type ProductsWorksheetRevision = IFinanceBaseWorksheetRevision

export type ProductsWorksheetDecisionDTO = yup.InferSubmit<typeof IFinanceBaseWorksheetDecisionDTOSchema>
